import * as React from 'react'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { HiDownload as DownloadIcon } from 'react-icons/hi'

import {
  ComponentWrapperContext,
  ResourceClickHandlerContext,
  SaveScheduleContext
} from '@react-ui/context'
import { ErrorWithDetailedMessage } from '../../notification/notification'
import { AiFillEyeInvisible as EyeIcon } from 'react-icons/ai'
import { PiCaretUpDownFill } from 'react-icons/pi'
import ToggleSwitch from '@builder/react-ui/scheduler/toggle-switch'
import SessionCalendar from '@builder/react-ui/scheduler/session-calendar'
import { Resource } from '@builder/resources'
import SessionCardMobile from '@builder/react-ui/scheduler/session-card-mobile/session-card-mobile'
import { IoPlayCircle as PlayIcon } from 'react-icons/io5'

export type StateType = 'completed' | 'current' | 'upcoming'
export interface SessionCardProps {
  id
  episodeNumber?: number
  numDaysFromStart: number
  skipped?: boolean
  date: any
  title: string
  thumbnail?: any
  type: 'link' | 'document' | 'video'
  session: any
  status: StateType
  weekendEpisodeNumber?: number
  trainingEpisodeNumber?: number
  totalWeekendSessions?: number
  category?: string
  sessionStatus?: StateType
  isMobile?: boolean
}

const SessionCard: FunctionComponent<
  SessionCardProps & { dragHandleProps?: any }
> = ({
  id,
  episodeNumber,
  date,
  title,
  thumbnail,
  type,
  session,
  skipped,
  status,
  numDaysFromStart,
  weekendEpisodeNumber,
  trainingEpisodeNumber,
  totalWeekendSessions,
  dragHandleProps,
  category,
  sessionStatus,
  isMobile = false
}) => {
  const { resourceClickHandler } = useContext(ResourceClickHandlerContext)
  const { alpha, userCanModify, skipSession, isCinderellaEnabled } =
    useContext(SaveScheduleContext)
  const [itemStatus, setItemStatus] = useState(sessionStatus)
  const [skippedValue, setSkippedValue] = useState(skipped)
  const [resource, setResource] = useState<Resource | undefined>()
  useEffect(() => {
    if (!alpha || !alpha.planning || !category) {
      console.warn(`Missing dependency(s):`, {
        alpha,
        planning: alpha?.planning,
        category
      })
      return
    }

    const collection = alpha.planning[category]
    if (!collection) {
      console.error(`No collection found for category: ${category}`)
      return
    }

    const found = collection.find((item) => item.id === id)
    if (found) {
      setResource(found)
    }
    setItemStatus(sessionStatus)
  }, [alpha, category, id, status, sessionStatus])
  useEffect(() => {
    setSkippedValue(skipped)
  }, [skipped, sessionStatus]) // Include sessionStatus if it affects rendering

  const { locale, isRtl } = useContext(ComponentWrapperContext)
  const formattedDate = new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  })

  const dayLabel = new Date(date).toLocaleDateString(locale, {
    weekday: 'long'
  })

  const Icon =
    skipped === true ? EyeIcon : type === 'document' ? DownloadIcon : PlayIcon
  const SizedIcon = () =>
    skipped ? (
      <div className='w-[40px] h-[40px] bg-[#EBEBEB] rounded-full flex items-center justify-center'>
        <EyeIcon className='w-5 h-5 text-black' />
      </div>
    ) : (
      <Icon color='#EBEBEB ' size='50px' />
    )
  const [error, setError] = useState<ErrorWithDetailedMessage>(null)

  const skippedLabel = $localize`:sessionCard.skippedLabel|Session skipped label - skipped label@@sessionSkipped:Skipped`
  const handleToggle = () => {
    skipSession(id, category)
  }
  const clickHandler = () => {
    if (!resource) {
      console.warn('No resource found for this session card.')
      return
    }
    if (skipped) {
      return
    } else {
      try {
        resourceClickHandler(resource)
      } catch (error) {
        const { message } = error
        error.detailedMessage = message

        error.message =
          `There was a issue with this resource` +
          `${session?.attachedDocument?.title ? `: ${session.attachedDocument.title}` : ''}`

        setError(error)
      }
    }
  }

  const mobileHeadingText = (() => {
    if (sessionStatus === 'completed' && !skipped)
      return $localize`:schedule.label|Schedule -
            Completed@@scheduleCompleted:Completed`
    if (skipped)
      return $localize`:schedule.label|Schedule -
            Skipped@@scheduleSkipped:Skipped`
    if (weekendEpisodeNumber && episodeNumber)
      return $localize`:schedule.label|Schedule - Weekend Label@@weekendLabel:Episode ${episodeNumber}:episode:${' - '}Holy Spirit Sessions ${weekendEpisodeNumber}:episode:/${totalWeekendSessions}:sessions:`
    if (trainingEpisodeNumber)
      return $localize`:schedule.label|Schedule - Training Number@@trainingNumber:Training ${trainingEpisodeNumber}:episode:`
    if (episodeNumber)
      return $localize`:schedule.label|Schedule - Episode Number@@episodeNumber:Episode ${episodeNumber}:episode:`
    return null
  })()

  const qaName = 'drag-drop-item' + id
  return isMobile ? (
    <SessionCardMobile
      qaId={`session-card-mobile-${id}`}
      thumbnail={thumbnail && isCinderellaEnabled ? thumbnail : ''}
      heading={mobileHeadingText}
      title={title}
      mediaType={type}
      date={formattedDate}
      isCompleted={sessionStatus === 'completed' && !skipped}
      isSkipped={skipped}
      isCurrent={sessionStatus === 'current'}
      dayLabel={dayLabel}
      clickHandler={() => clickHandler()}
      toggleSkip={() => skipSession(id, category)}
      calendar={
        <SessionCalendar
          id={id}
          category={category}
          isDisabled={skipped}
          sessionDate={date}
          isMobile
          isTraining={Boolean(trainingEpisodeNumber)}
        />
      }
    />
  ) : (
    <>
      <div
        data-qa-name={`session-card-${id}`}
        className='flex p-[12px] shadow-xl has-[#drag-btn:active]:border-black has-[#drag-btn:active]:border-2  gap-5 rounded-lg bg-alpha-white border cursor-auto w-full justify-between items-center'
        style={{
          border: sessionStatus === 'current' ? `solid 2px #000000` : '',
          boxShadow: '0px 0px 16px  #cccccc'
        }}
      >
        <div
          data-qa-name={`session-click-container`}
          onClick={clickHandler}
          className={`click-container flex gap-5 ${skipped ? '' : 'cursor-pointer'}`}
        >
          {thumbnail && isCinderellaEnabled ? (
            <>
              <div
                data-qa-name='session-thumbnail'
                className={`h-[76px] aspect-video bg-cover bg-center flex justify-center items-center text-alpha-grey-light rounded-lg ${skipped ? 'opacity-50' : ''}`}
                style={{
                  backgroundImage: `url("${thumbnail?.url}")`,
                  borderRadius: '4px'
                }}
              >
                <SizedIcon />
              </div>
            </>
          ) : null}

          <div className='title-container flex flex-col justify-center h-[76px]'>
            <div className='session-number-container flex items-center gap-2'>
              {trainingEpisodeNumber ? (
                <span
                  data-qa-name='training-label'
                  className='text-xs text-alpha-oatmeal-30 font-bold'
                >
                  {$localize`:schedule.label|Schedule - Training Number@@trainingNumber:Training ${trainingEpisodeNumber}:episode:`}
                </span>
              ) : null}
              {episodeNumber ? (
                <span
                  data-qa-name='episode-label'
                  className='text-xs text-alpha-oatmeal-30 font-bold'
                >
                  {$localize`:schedule.label|Schedule - Episode Number@@episodeNumber:Episode ${episodeNumber}:episode:`}
                </span>
              ) : null}

              {weekendEpisodeNumber ? (
                <>
                  <span className='text-alpha-oatmeal-30'>•</span>
                  <span
                    data-qa-name='weekend-label'
                    className={`text-xs font-bold ${skipped ? 'text-alpha-oatmeal-30' : 'text-alpha-red'}`}
                  >
                    {$localize`:schedule.label|Schedule - Weekend Label@@weekendLabel:Holy Spirit Sessions ${weekendEpisodeNumber}:episode:/${totalWeekendSessions}:sessions:`}
                  </span>
                </>
              ) : null}
            </div>
            <h5
              data-qa-name='session-title'
              className={`m-0 text-base ${skipped ? 'text-alpha-oatmeal-30' : ''}`}
            >
              {title}
            </h5>
          </div>
        </div>
        <div className='right-section flex'>
          <div className='flex h-fit gap-5 items-center w-[125px]'>
            <div className='inline-block h-[40px] min-h-[1em] w-0.5 bg-alpha-oatmeal-10 '></div>

            <div className='date-container flex flex-col '>
              <span
                data-qa-name='day-label'
                className='text-xs font-bold
                text-alpha-oatmeal-30'
              >
                {skipped ? '-' : dayLabel}
              </span>
              <span
                data-qa-name='date-label'
                className={`text-base ${skipped ? 'text-alpha-oatmeal-30' : ''}`}
              >
                {skipped ? skippedLabel : formattedDate}
              </span>
            </div>
          </div>

          {userCanModify ? (
            <>
              {' '}
              <div className='flex items-center gap-4'>
                <div
                  className={`min-w-[90px] flex justify-end ${
                    status === 'completed' && !skipped
                      ? isRtl
                        ? 'ml-[170px]'
                        : 'mr-[170px]'
                      : ''
                  }`}
                >
                  <SessionCalendar
                    id={id}
                    category={category}
                    isDisabled={skipped}
                    sessionDate={date}
                    isTraining={Boolean(trainingEpisodeNumber)}
                  />
                </div>
                {skipped || status !== 'completed' ? (
                  <>
                    <div className='inline-block h-[40px] min-h-[1em] w-0.5 bg-alpha-oatmeal-10'></div>
                    <ToggleSwitch
                      toggleDisabled={false} //change
                      toggleValue={skippedValue}
                      handleToggle={handleToggle}
                    />
                  </>
                ) : null}
                {/* Drag icon inside the card */}
                {(status == 'completed' && skipped) || status != 'completed' ? (
                  <>
                    <div
                      className={`flex items-center cursor-grab group  `}
                      {...dragHandleProps} //change
                    >
                      <PiCaretUpDownFill
                        size={32}
                        data-qa-name={qaName}
                        id='drag-btn'
                        className={`group-hover:text-alpha-red transition-colors group-hover:bg-alpha-oatmeal rounded-full p-2 text-current`}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default SessionCard
